"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowUpRight, BellOff, FileSpreadsheet, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "@/store/api/notificationApi";
import { BatchProgressCard } from "./batch-progress-card";
import { formatDateTime } from "@/lib/utils/credit.utils";
import { cn } from "@/lib/utils/utils";
import type { AppNotification } from "@/lib/types/notification.types";

interface NotificationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
}

/**
 * The bell's side panel: the most recent notifications, newest first. Clicking
 * one marks it read and opens its export record under Settings > Exports.
 * "View all" hands off to the full paginated Notifications page instead.
 */
export function NotificationPanel({
  open,
  onOpenChange,
  notifications,
  unreadCount,
  isLoading,
}: NotificationPanelProps) {
  const router = useRouter();
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead, { isLoading: isMarkingAll }] = useMarkAllNotificationsReadMutation();

  // Downloading is deliberately not offered here. The export row is dropped
  // once its link expires, so a button in this panel would be dead for any
  // notification older than that with no way to tell the user why. Selecting
  // one opens Settings > Exports, which reads the export log directly and
  // already renders an expired row as expired.
  const handleSelect = (notification: AppNotification) => {
    if (!notification.read) {
      markRead(notification._id);
    }
    onOpenChange(false);
    router.push("/settings/exports");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Same shape as sidebar-user.tsx -- see the note there for why each of
          translate-x-0!, sm:max-w-none and z-55 is load-bearing. */}
      <SheetContent
        side="left"
        showOverlay={false}
        className="fixed left-18! top-0 z-55 flex h-screen w-90 min-w-90 flex-col rounded-none border-r bg-background p-0 shadow-xl sm:max-w-none [&]:translate-x-0! transition-opacity"
      >
        <SheetHeader className="flex flex-row items-center justify-between border-b px-4 py-3">
          <SheetTitle className="flex items-center gap-2 font-semibold">
            Notifications
            {unreadCount > 0 ? (
              <Badge
                variant="outline"
                className="border-brand/25 px-2 py-0.5 text-[11px] font-medium text-brand"
              >
                {unreadCount} new
              </Badge>
            ) : null}
          </SheetTitle>

          <div className="flex items-center gap-1">
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => markAllRead()}
                disabled={isMarkingAll}
                className="cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-brand transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
              >
                Mark all read
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-3 min-h-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={`notification-skeleton-${index}`} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <BellOff />
                </EmptyMedia>
                <EmptyTitle>Nothing here yet.</EmptyTitle>
                <EmptyDescription>
                  You&apos;re all caught up — export updates will show up here.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <button
                  key={notification._id}
                  type="button"
                  onClick={() => handleSelect(notification)}
                  className={cn(
                    "group flex w-full cursor-pointer items-start gap-3 rounded-lg border border-transparent px-3 py-3 text-left text-sm transition-colors",
                    "hover:border-border hover:bg-muted/40",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
                    !notification.read && "bg-muted/30"
                  )}
                >
                  {/* Decorative: marks the row as an export. Not a control --
                      see handleSelect for why downloading isn't offered here. */}
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 transition-colors",
                      notification.read ? "text-muted-foreground" : "text-brand/70"
                    )}
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          "truncate text-foreground",
                          notification.read ? "font-medium" : "font-semibold"
                        )}
                      >
                        {notification.title}
                      </span>
                      {!notification.read ? (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                      ) : null}
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                      {notification.message}
                    </span>
                    <span className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-muted-foreground/80">
                        {formatDateTime(notification.createdAt)}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand opacity-0 transition-opacity group-hover:opacity-100 cursor-pointer">
                            View export
                            <ArrowUpRight className="h-3 w-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View export record</p>
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Below the notification list and above "View all": a bulk run is
            live state the user is waiting on, so it stays in view while the
            list above scrolls. Only mounted while the panel is open, which is
            also when its list endpoints are polled (see useBulkBatchTracking). */}
        <BatchProgressCard />

        <div className="shrink-0 border-t px-4 py-3">
          <Link
            href="/notifications"
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-sm font-medium text-brand transition-colors hover:bg-muted/50"
          >
            View all notifications
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NotificationPanel;
